import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getDevice } from '../services/devices';
import { getBattery } from '../services/batteries';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { VendorRow } from '../components/ui/VendorRow';

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();

  const deviceQ = useQuery({
    queryKey: ['device', id],
    queryFn: () => getDevice(id!),
    enabled: !!id,
  });

  const batteryQ = useQuery({
    queryKey: ['battery', deviceQ.data?.batteryId],
    queryFn: () => getBattery(deviceQ.data!.batteryId),
    enabled: !!deviceQ.data?.batteryId,
  });

  if (deviceQ.isLoading) return <Spinner />;
  if (deviceQ.isError || !deviceQ.data) {
    return <Card className="text-accent-red">Device not found.</Card>;
  }

  const device = deviceQ.data;

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col sm:flex-row gap-6">
        <div className="sm:w-1/3 aspect-video rounded-xl2 bg-surface-700 overflow-hidden flex items-center justify-center">
          {device.imageUrl ? (
            <img src={device.imageUrl} alt={device.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-neutral-500 text-sm">No image</span>
          )}
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase text-neutral-400">{device.brand}</div>
          <h1>{device.name}</h1>
          <div className="mt-2 inline-block text-xs bg-surface-700 rounded-full px-3 py-1 text-neutral-300">
            {device.category}
          </div>
          {device.replacementGuideUrl && (
            <div className="mt-4">
              <a
                href={device.replacementGuideUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-lime-400 hover:underline text-sm"
              >
                How to replace →
              </a>
            </div>
          )}
        </div>
      </header>

      <section>
        <h2 className="mb-4">Compatible battery</h2>
        {batteryQ.isLoading && <Spinner />}
        {batteryQ.isError && (
          <Card className="text-accent-red">Could not load battery details.</Card>
        )}
        {batteryQ.isSuccess && (
          <Card>
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div>
                <div className="text-xs uppercase text-neutral-400">
                  {batteryQ.data.battery.chemistry}
                </div>
                <div className="text-xl font-semibold">{batteryQ.data.battery.modelCode}</div>
                <div className="text-sm text-neutral-400 mt-1">
                  {batteryQ.data.battery.capacityMah} mAh · {batteryQ.data.battery.voltage} V
                </div>
              </div>
              <Link
                to={`/batteries/${batteryQ.data.battery._id}`}
                className="btn-secondary self-start"
              >
                View spec sheet
              </Link>
            </div>
          </Card>
        )}
      </section>

      <section>
        <h2 className="mb-4">Buy from ranked vendors</h2>
        {batteryQ.isLoading && <Spinner />}
        {batteryQ.isSuccess && batteryQ.data.vendors.length === 0 && (
          <Card className="text-neutral-400">No vendors available yet.</Card>
        )}
        {batteryQ.isSuccess && batteryQ.data.vendors.length > 0 && (
          <ul className="flex flex-col gap-3">
            {batteryQ.data.vendors
              .slice()
              .sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99))
              .map((v, i) => (
                <li key={v._id}>
                  <VendorRow vendor={v} rank={v.rank ?? i + 1} />
                </li>
              ))}
          </ul>
        )}
      </section>
    </div>
  );
}
